// Fill the Malca Courier template with a job's data and return a downloadable
// Blob. Uses ExcelJS so the template's visual formatting (colors, borders,
// merges, column widths, fonts) and formulas survive the round-trip.
//
// Cell map (verified against owner's filled examples):
//   E9        → Detail date                (formatted Excel date)
//   K9        → Job #
//   D11       → Transfer type              (Outbound / Inbound / Direct)
//   L11       → Airport code
//   E12       → Pickup location            (= airport code for inbound, blank otherwise)
//   E13..E21  → Location A..I names        (non-airport stops, in route order)
//   J13..J21  → POD HAWB # / note          ("Scheduled in error" for skipped)
//   E25       → Arrival at pickup          (auto: 30 min before earliest scheduled)
//   E26       → Time departed pickup       (= latest pickup completion time)
//   E28..E36  → Delivery time pkg A..I     (matches the location rows above)
//   E37       → Est arrival back at pickup
//   L27       → Starting mileage           (dispatch → first stop)
//   L28       → Inbound only: airport → first delivery (blank for outbound)
//   L29..L36  → Leg between successive stops; for outbound the last entry is
//               the leg into the airport
//   L37       → Return leg                 (airport → first pickup for outbound,
//                                           last delivery → airport for inbound)
//   C40/F40   → Single consolidated expense (amount in C40:D40, details in F40:H40)
//   A43       → Additional comments        (= billingNotes)
//
// All mileage values are rounded UP to whole numbers per Orion FAQ:
// "if the mileage is fractional, please round up to the next whole number".
//
// Template formulas (D48/E48/F48/F49/F50/L50) compute hours/$ totals when
// Excel opens the file. We set fullCalcOnLoad so they recalculate immediately.

import ExcelJS from "exceljs";

const TEMPLATE_URL = "/templates/malca-2026.xlsx";

// ── Helpers ──────────────────────────────────────────────────────────

function tsToDate(ts) {
  if (!ts) return null;
  let ms = null;
  if (typeof ts === "number") ms = ts;
  else if (typeof ts === "string") ms = Date.parse(ts);
  else if (ts.toMillis) ms = ts.toMillis();
  else if (ts.seconds != null) ms = ts.seconds * 1000;
  if (ms == null || Number.isNaN(ms)) return null;
  return new Date(ms);
}

function tsToHHMM(ts) {
  const d = tsToDate(ts);
  if (!d) return null;
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

// HH:MM string → fractional-day number Excel treats as a time.
function hhmmToSerial(hhmm) {
  if (!hhmm) return null;
  const m = /^(\d{1,2}):(\d{2})$/.exec(String(hhmm).trim());
  if (!m) return null;
  const h = Number(m[1]);
  const mi = Number(m[2]);
  if (h > 23 || mi > 59) return null;
  return h / 24 + mi / 1440;
}

function minutesOf(hhmm) {
  if (!hhmm) return null;
  const m = /^(\d{1,2}):(\d{2})$/.exec(String(hhmm).trim());
  if (!m) return null;
  return Number(m[1]) * 60 + Number(m[2]);
}

function setVal(ws, addr, value, alignment) {
  if (value === null || value === undefined || value === "") return;
  const cell = ws.getCell(addr);
  cell.value = value;
  // Merge the extra alignment into whatever the template already set, so a
  // centred cell stays centred. Clone the style first — cells in the
  // template share style objects, and mutating one would move the others.
  if (alignment) {
    cell.style = { ...(cell.style || {}), alignment: { ...(cell.alignment || {}), ...alignment } };
  }
}

function setTime(ws, addr, hhmm) {
  const serial = hhmmToSerial(hhmm);
  if (serial == null) return;
  const cell = ws.getCell(addr);
  cell.value = serial;
  // Force 24-hour military format. The template's existing style may include
  // an "h:mm AM/PM" numFmt or be locale-dependent — overriding only via
  // `cell.numFmt` doesn't always stick when the cell shares a style object
  // with other cells. Clone the style so our override is on a fresh instance,
  // and write to both `numFmt` and the style's numFmt for belt-and-suspenders.
  const newStyle = cell.style ? { ...cell.style } : {};
  newStyle.numFmt = "hh:mm";
  cell.style = newStyle;
  cell.numFmt = "hh:mm";
}

function prettyTransferType(type) {
  if (!type) return "";
  const t = String(type).toLowerCase();
  if (t === "outbound") return "Outbound";
  if (t === "inbound") return "Inbound";
  if (t === "direct") return "Direct";
  if (t === "roundtrip") return "Round Trip";
  return type;
}

// Packages live at job.packages[] keyed by stopId. We try several matching
// strategies in order, so HAWBs still surface even when older data has a
// missing or mismatched stopId (e.g. stops added in the field before we
// started giving them ids):
//   1. p.stopId === stop.id            (canonical)
//   2. p.stopIndex === stops.indexOf   (legacy shape from AI parsing)
//   3. p.locationName === stop.locationName  (last-resort name match)
//   4. stop.packages[]                 (very-old nested shape)
function stopHawbList(stop, job) {
  if (!stop) return [];
  const all = Array.isArray(job?.packages) ? job.packages : null;
  const stops = Array.isArray(job?.stops) ? job.stops : [];
  const stopIdx = stops.indexOf(stop);
  let pkgs = [];
  if (all && stop.id) {
    pkgs = all.filter((p) => p?.stopId === stop.id);
  }
  if (pkgs.length === 0 && all && stopIdx >= 0) {
    pkgs = all.filter((p) => p?.stopIndex === stopIdx);
  }
  if (pkgs.length === 0 && all && stop.locationName) {
    const norm = (s) => String(s || "").trim().toLowerCase();
    pkgs = all.filter((p) => norm(p?.locationName) === norm(stop.locationName));
  }
  if (pkgs.length === 0 && Array.isArray(stop.packages)) {
    pkgs = stop.packages;
  }
  //   5. Vaulted away — the piece was attempted here but rode on to a later
  //      run, so it's gone from job.packages. Malca still wants the POD #
  //      against the location that was actually driven to.
  if (pkgs.length === 0 && Array.isArray(job?.vaultedAway) && stop.id) {
    pkgs = job.vaultedAway.filter((v) => v?.stopId === stop.id);
  }
  return pkgs
    .map((p) => p?.hawbNumber || p?.hawb || p?.trackingNumber || p?.awb || p?.reference)
    .filter(Boolean)
    // Sort by POD # (natural/numeric order) so a location's packages read in
    // sequence — much easier to verify a big list is all there.
    .sort((a, b) => String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: "base" }));
}

// Round mileage UP per Orion FAQ ("12.7 = 13"). Zero stays zero.
function ceilMiles(v) {
  const n = Number(v);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.ceil(n);
}

// ── Main ─────────────────────────────────────────────────────────────

/**
 * Build the filled workbook as a Blob ready for download.
 *
 * For multi-guard jobs, generate one workbook per assigned guard. Drivers
 * (guards #1 and #3) get the mileage filled in; non-drivers get zero mileage
 * but the same hours and locations.
 *
 * @param {object} job
 * @param {object} [broker]
 * @param {object} [opts]
 * @param {number} [opts.guardNumber] — 1..4. If omitted, treat as single-guard.
 * @param {string} [opts.guardName]   — annotates A43 ("Activity log for: ...").
 * @param {boolean}[opts.isDriver]    — when false, blank out all mileage cells.
 * @returns {Promise<Blob>}
 */
export async function generateMalcaWorkbook(job, broker /* eslint-disable-line no-unused-vars */, opts = {}) {
  const res = await fetch(TEMPLATE_URL);
  if (!res.ok) throw new Error(`Couldn't load Malca template (${res.status}). Make sure public/templates/malca-2026.xlsx exists.`);
  const ab = await res.arrayBuffer();

  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(ab);
  const ws = wb.worksheets[0];

  wb.calcProperties = wb.calcProperties || {};
  wb.calcProperties.fullCalcOnLoad = true;

  const stops = Array.isArray(job.stops) ? job.stops : [];
  const jobTypeLc = String(job.jobType || job.type).toLowerCase();
  const isInbound = jobTypeLc === "inbound";
  // Round trip fills the front of the form like an inbound (starts at the
  // airport, per-row location times, inbound mileage offsets); the end time
  // (E37) already carries the outbound rule via job.returnTime.
  const isRoundtrip = jobTypeLc === "roundtrip";
  const isDirect = jobTypeLc === "direct";
  // Stops in route order minus airport — Malca's "Location A..I" list excludes
  // the airport row, which is implicit on inbound and shows as the return leg
  // on outbound.
  const locStops = stops.filter((s) => s.type !== "airport");
  const airportCode = String(job.airportCode || "").toUpperCase();

  // ── Header (E9 / K9 / D11 / L11 / E12) ─────────────────────────────
  // Detail date = the SCHEDULED job date (what the Review screen shows and
  // the day the detail ran). Prefer it over completedAt — completedAt is when
  // the job was marked done, which can be a different day (past-midnight
  // finish, or a cancelled trip stamped when the owner billed it). Fall back
  // to the completion/started timestamps only when there's no scheduled date.
  let detailDate = null;
  if (job.scheduledDate) {
    const [y, m, d] = String(job.scheduledDate).split("-").map(Number);
    if (y && m && d) detailDate = new Date(y, m - 1, d, 12, 0, 0); // local noon — no TZ shift
  }
  if (!detailDate) detailDate = tsToDate(job.completedAt || job.completedAtMs || job.startedAt);
  if (detailDate) {
    const c = ws.getCell("E9");
    c.value = detailDate;
    // Always force the date format — relying on the template's existing
    // numFmt sometimes leaves a stale "general" formatting from previous
    // runs that displays as a number serial.
    const dateStyle = c.style ? { ...c.style } : {};
    dateStyle.numFmt = "dddd, mmmm d, yyyy";
    c.style = dateStyle;
    c.numFmt = "dddd, mmmm d, yyyy";
  }
  setVal(ws, "K9", job.jobNumber || job.id || "");
  setVal(ws, "D11", prettyTransferType(job.jobType || job.type));
  setVal(ws, "L11", airportCode);
  // Pickup location: the airport code for inbound/roundtrip (both start
  // there), blank for outbound/direct.
  if (isInbound || isRoundtrip) setVal(ws, "E12", airportCode);

  // A location whose pieces never flew — the flight was cancelled, or the
  // packages weren't on the plane — reads "No Show" in the HAWB column.
  // Other no-shows (e.g. a stop scheduled in error) keep their reason.
  const isFlightNoShow = (stop) => {
    if (job.tripCancelled) return true;
    if (stop.noPackagesReceived) return true;
    if (/flight|plane|cancel|arriv/i.test(String(stop.noShowReason || ""))) return true;
    const pkgs = (job.packages || []).filter((p) => p.stopId === stop.id);
    return pkgs.length > 0 && pkgs.every((p) => p.notOnFlight);
  };

  // ── Locations A..I + HAWBs (rows 13..21) ───────────────────────────
  // Build one worksheet row per location — but a location with MORE THAN 4
  // packages spills onto extra rows: the same location name, the next 4 POD #s
  // on each. So 7 PODs at "JC Penney" → row 1 (JC Penney, 4 PODs), row 2
  // (JC Penney, next 3). Capped at the template's 9 location rows (13..21).
  const HAWBS_PER_ROW = 4;
  const worksheetRows = [];
  for (const stop of locStops) {
    const name = stop.locationName || "";
    if (isFlightNoShow(stop)) { worksheetRows.push({ name, hawb: "No Show", stop }); continue; }
    if (stop.noShow) { worksheetRows.push({ name, hawb: stop.noShowReason || "Scheduled in error", stop }); continue; }
    const ids = stopHawbList(stop, job);
    if (ids.length === 0) { worksheetRows.push({ name, hawb: "", stop }); continue; }
    for (let i = 0; i < ids.length; i += HAWBS_PER_ROW) {
      worksheetRows.push({ name, hawb: ids.slice(i, i + HAWBS_PER_ROW).join(", "), stop });
    }
  }
  const filledRows = worksheetRows.slice(0, 9);
  filledRows.forEach((r, idx) => {
    const row = 13 + idx;
    // The name cell is merged E:G at a fixed width, and a merged cell clips
    // rather than spilling into its neighbour — so "Four Points by Sheraton
    // Raleigh Durham Airport" came out cut off mid-word. shrinkToFit scales
    // the type down until the whole name fits, which keeps Malca's form
    // exactly as they laid it out. Widening the column would shift the rest
    // of their sheet.
    setVal(ws, `E${row}`, r.name, { shrinkToFit: true });
    if (r.hawb) setVal(ws, `J${row}`, r.hawb);
  });

  // ── Times ──────────────────────────────────────────────────────────
  // E25 = arrival at pickup. PER-GUARD: a guard who joined late (approved
  // partway through) starts their own clock, so their sheet must not open
  // with the job's start. The review panel resolves each guard's effective
  // start into billedStartTime; fall back to the job for older records.
  const thisGuard = (Array.isArray(job.guardAssignments) ? job.guardAssignments : [])
    .find((g) => g.guardNumber === opts.guardNumber);
  const guardStartTime = thisGuard?.billedStartTime || job.pickupStartTime;
  setTime(ws, "E25", guardStartTime);

  // E26 = "time departed pickup" — the latest pickup-phase time, i.e. when
  // the guard finished collecting packages and started toward the next phase.
  // Outbound: latest of the (non-airport) pickup billing times.
  // Inbound:  the airport stop's billing time (departure with packages).
  const stopTimes = Array.isArray(job.stopBillingTimes) ? job.stopBillingTimes : [];
  // Bill checkbox per stop — an unchecked stop (e.g. a vaulted piece
  // already picked up on a prior job, carried on this manifest but not
  // re-billed) must NOT contribute its time to this job's time cells.
  const stopBillableArr = Array.isArray(job.stopBillable) ? job.stopBillable : [];
  function isBilled(stop) {
    return stopBillableArr[stops.indexOf(stop)] !== false;
  }
  function pickupCollectedTime(stop) {
    const fullIdx = stops.indexOf(stop);
    return stopTimes[fullIdx] || tsToHHMM(stop.completedAt) || stop.scheduledTime || null;
  }
  let departedPickup = null;
  if (isInbound || isRoundtrip) {
    // find() takes the FIRST airport — on a round trip that's the arrival
    // bookend, which is exactly the "departed pickup" we want.
    const airport = stops.find((s) => s.type === "airport");
    // A vault delivery doesn't go through the airport (its Bill box is
    // unchecked) — its stale completion time isn't a real "departed pickup".
    // Fall back to the leave-home/arrival time (no pickup wait on a home-based
    // run). A normal inbound uses the airport's billing time as before.
    if (airport && isBilled(airport)) {
      departedPickup = pickupCollectedTime(airport);
    } else {
      departedPickup = job.pickupStartTime || null;
    }
  } else if (isDirect) {
    // A direct is one pickup to one delivery, so "departed pickup" is the
    // time at the PICKUP. Taking the latest stop time (the outbound rule
    // below) handed it the DELIVERY time instead — on a 20:30 pickup with a
    // 23:00 delivery the sheet read as if the guard left the pickup at 23:00.
    const pickup = locStops.find((s) => isBilled(s) && String(s.type || "").toLowerCase() === "pickup")
      || locStops.find((s) => isBilled(s));
    departedPickup = pickup ? pickupCollectedTime(pickup) : null;
  } else {
    // Outbound: pick the LAST (latest) of the BILLED pickup times — a guard
    // collecting from several shippers departs after the final one.
    let bestMin = -1;
    for (const stop of locStops) {
      if (!isBilled(stop)) continue;
      const t = pickupCollectedTime(stop);
      const m = minutesOf(t);
      if (m != null && m > bestMin) {
        bestMin = m;
        departedPickup = t;
      }
    }
  }
  setTime(ws, "E26", departedPickup);

  // E28..E36 = per-package delivery times.
  //
  // Outbound: every package "delivered" at the airport when the plane leaves.
  // The convention is to enter (flight time + 15 min) ONCE on E28 and leave
  // E29..E36 blank — the single entry stands for all packages on that flight.
  //
  // Inbound: each delivery happens at a different stop, so we list per-stop
  // times in row order matching the location rows above.
  if (isInbound || isRoundtrip || isDirect) {
    // One row per worksheet row (splits included) — a location split across
    // rows carries the same delivery time on each of its rows. On a round
    // trip pickup rows carry the collection time at that location.
    // A direct has no flight, so the branch below left its delivery cell
    // empty; its delivery time is simply the time at the delivery stop.
    // On an inbound or round trip the rows and the packages line up — each
    // delivery location has its own package — so the time goes on the
    // location's own row. A DIRECT breaks that: two locations (pickup then
    // delivery) but one package, so the package's time belongs on the FIRST
    // delivery-time line, not on the delivery location's row. Count the
    // delivery rows separately for directs.
    let directSlot = 0;
    filledRows.forEach((r, idx) => {
      if (!isBilled(r.stop)) return; // skip an unchecked stop's time, keep row alignment
      const t = pickupCollectedTime(r.stop);
      if (!t) return;
      if (isDirect) {
        // The pickup's time is E26 ("time departed pickup") — it isn't a
        // delivery and must not take a delivery line.
        if (String(r.stop?.type || "").toLowerCase() === "pickup") return;
        setTime(ws, `E${28 + directSlot}`, t);
        directSlot += 1;
        return;
      }
      setTime(ws, `E${28 + idx}`, t);
    });
  } else if (thisGuard?.billedDeliveryTime) {
    // A guard relieved at the airport handed the packages over on arrival —
    // wheels-up + 15 is only the hand-over for whoever stayed with the plane.
    // The review panel resolves which is which and stamps the time here.
    setTime(ws, "E28", thisGuard.billedDeliveryTime);
  } else {
    // Outbound (and direct, which has no airport flight time anyway).
    const fm = minutesOf(job.flightTime);
    if (fm != null) {
      const deliveryMinutes = fm + 15;
      const hh = String(Math.floor(deliveryMinutes / 60) % 24).padStart(2, "0");
      const mm = String(deliveryMinutes % 60).padStart(2, "0");
      setTime(ws, "E28", `${hh}:${mm}`);
    }
  }

  // E37 = est arrival back at pickup (after flight + return drive on outbound,
  // after last delivery → airport on inbound). PER-GUARD overrideable —
  // if the guard split off early, their workbook reflects their actual
  // shorter end time, not the route's full return time.
  // billedEndTime is the end the review panel actually billed — a typed
  // override OR a computed default such as the relieved-at-the-airport time
  // on a multi-guard outbound. endTime alone holds only what was typed, so
  // a relieved guard read as blank and inherited the route's full return.
  const guardEndTime = thisGuard?.billedEndTime || thisGuard?.endTime || job.returnTime;
  if (guardEndTime) setTime(ws, "E37", guardEndTime);

  // Force military hh:mm formatting on every time cell in the block —
  // and CLEAR cells we didn't write to (so they show blank instead of
  // the template's stale "0:00"). The template ships with some cells
  // pre-formatted as "h:mm AM/PM" and "h:mm" (single-digit hour), so
  // we override every address to be consistent.
  const allTimeAddrs = [
    "E25", "E26",
    "E28", "E29", "E30", "E31", "E32", "E33", "E34", "E35", "E36",
    "E37",
  ];
  for (const addr of allTimeAddrs) {
    const cell = ws.getCell(addr);
    // Belt-and-suspenders: override style AND numFmt so the cell can
    // never inherit the template's 12-hour or single-digit-hour format.
    const newStyle = cell.style ? { ...cell.style } : {};
    newStyle.numFmt = "hh:mm";
    cell.style = newStyle;
    cell.numFmt = "hh:mm";
    // If we never wrote a real time value to this cell, blank it out.
    // ExcelJS leaves 0 in unwritten cells which would format as "00:00".
    if (cell.value === 0 || cell.value === null || cell.value === undefined) {
      cell.value = null;
    }
  }

  // ── Mileage (column L) ─────────────────────────────────────────────
  // segments[] layout (length = stops.length + 1):
  //   [0]      = dispatch → stops[0] (starting mileage)
  //   [1..N-1] = stops[i-1] → stops[i] (between-stop legs)
  //   [N]      = return leg
  //
  // Template layout:
  //   L27 = starting mileage
  //   L28 = airport → first delivery (INBOUND only; blank on outbound)
  //   L29..L36 = sequential between-stop legs
  //   L37 = return
  // Per-guard mileage rule: only drivers (#1, #3) submit mileage. Non-drivers
  // file an activity log with all mileage cells blank. opts.isDriver=false
  // explicitly suppresses; otherwise (single-guard or driver) we fill normally.
  const fillMileage = opts.isDriver !== false;
  // Per-guard segments (from the per-stop coverage) win when present — they
  // hold this car's ACTUAL legs (0 for legs it didn't drive, the back-drive
  // in the return cell). Falls back to the full route's segments. Hoisted
  // out of the fill block so the cached-totals stamp below can sum them.
  const segs = Array.isArray(opts.billedSegments) && opts.billedSegments.length
    ? opts.billedSegments
    : (Array.isArray(job.mileageSegments) ? job.mileageSegments : []);
  if (fillMileage) {
    setVal(ws, "L27", ceilMiles(segs[0]));

    // For inbound: leg j (segments[j], j=1..N-1) → row L(27 + j)
    //   so segs[1] (CLT→Cust1) → L28, segs[2] (Cust1→Cust2) → L29, ...
    // For outbound: leg j → row L(28 + j); L28 stays blank.
    //   so segs[1] (Pickup1→Pickup2) → L29, ..., segs[N-1] (lastPickup→airport) → L(27+N)
    const rowOffset = (isInbound || isRoundtrip) ? 27 : 28;
    for (let j = 1; j < stops.length; j++) {
      const row = rowOffset + j;
      if (row > 36) break;
      setVal(ws, `L${row}`, ceilMiles(segs[j]));
    }
    // Return leg always at L37.
    setVal(ws, "L37", ceilMiles(segs[segs.length - 1]));
  }

  // ── Expenses ───────────────────────────────────────────────────────
  // Itemize: each expense line the owner entered on the review screen lands on
  // its own row so the broker sees the breakdown (e.g. a vault fee + a toll),
  // not one lump. Amount cells C40:C42 / J40:J42 are what the template sums
  // (F50 = SUM(C40:D42, J40:K42)); details go in F40:F42 / M40:M42. Round-trip
  // items bill 2x (matches the review total). Falls back to the single
  // consolidated value for older jobs with no itemized expenses.
  const expItems = Array.isArray(job.expenseItems)
    ? job.expenseItems.filter((e) => Number(e.amount) > 0)
    : [];
  const amtCells = ["C40", "C41", "C42", "J40", "J41", "J42"];
  const detCells = ["F40", "F41", "F42", "M40", "M41", "M42"];
  const itemAmt = (e) => (Number(e.amount) || 0) * (e.roundTrip ? 2 : 1);
  if (expItems.length) {
    expItems.slice(0, amtCells.length).forEach((e, i) => {
      // The last visible slot absorbs any overflow beyond the 6 rows.
      const overflow = i === amtCells.length - 1 && expItems.length > amtCells.length;
      const amt = overflow
        ? expItems.slice(i).reduce((s, x) => s + itemAmt(x), 0)
        : itemAmt(e);
      setVal(ws, amtCells[i], amt);
      setVal(ws, detCells[i], overflow ? "Other expenses" : (e.label || "Expense"));
    });
  } else {
    const exp = Number(job.expenses);
    if (Number.isFinite(exp) && exp > 0) {
      setVal(ws, "C40", exp);
      setVal(ws, "F40", job.expensesDetail || "Expenses");
    }
  }

  // ── Activity log header (A43) + body (A44) ─────────────────────────
  // A43 is the merged title bar ("Activity log for: Guard X — Name") and
  // A44 is the actual notes body that AP reads. Per Orion FAQ: "If the
  // total calculated time goes over 3 hours, please make a quick comment
  // in the additional comments section..." Stop notes (the textarea the
  // guard fills on each stop card) get prefixed with the location name so
  // the rep can see what happened where without having to call us back.
  if (opts.guardNumber || opts.guardName) {
    // Just the slot number — guard name is already implicit from the
    // per-guard packet of invoices and clutters the header otherwise.
    const tag = `Guard ${opts.guardNumber || ""}`.trim();
    setVal(
      ws,
      "A43",
      `Activity log for: ${tag}${opts.isDriver === false ? " (non-driver — no mileage)" : ""}`,
    );
  }
  const bodyParts = [];
  if (job.billingNotes) bodyParts.push(job.billingNotes);
  const stopNotes = stops
    .map((s) => {
      const n = (s.note || "").trim();
      if (!n) return null;
      const where = s.locationName || s.address || "stop";
      return `${where}: ${n}`;
    })
    .filter(Boolean);
  if (stopNotes.length) bodyParts.push(stopNotes.join(" · "));
  if (bodyParts.length) setVal(ws, "A44", bodyParts.join(" — "));

  // ── Cached formula results ──────────────────────────────────────────
  // The template's totals (hours / mileage / $) are formulas that Excel
  // recomputes on open via fullCalcOnLoad. But anything that skips the
  // recalculation — Protected View before "Enable Editing", attachment
  // previews, phone viewers — displays the STORED result instead, and the
  // template ships those empty (a total can read blank or stale while the
  // leg cells look right). Stamp the computed results into the formula
  // cells so the totals read correctly everywhere; a real recalc produces
  // the same numbers. Mirrors the template's own formulas exactly:
  //   D48 = MOD(E37-E25,1)*24          raw hours
  //   E48 = ROUND(max(4, D48), 2)      4h Malca minimum
  //   F48 = E48*40                     hourly $
  //   D49 = SUM(L27:M37)               total miles
  //   F49 = D49*0.725                  mileage $
  //   F50 = SUM(C40:D42,J40:K42)       expenses
  //   L50 = SUM(F48:G50)               grand total
  const setCachedResult = (addr, result) => {
    if (result == null || !Number.isFinite(result)) return;
    const cell = ws.getCell(addr);
    const v = cell.value;
    if (v && typeof v === "object" && v.formula) {
      cell.value = { formula: v.formula, result: Math.round(result * 100) / 100 };
    }
  };
  const milesTotal = fillMileage
    ? segs.reduce((s, m) => s + ceilMiles(m), 0)
    : 0;
  const expTotal = expItems.length
    ? expItems.reduce((s, e) => s + itemAmt(e), 0)
    : (Number.isFinite(Number(job.expenses)) && Number(job.expenses) > 0 ? Number(job.expenses) : 0);
  setCachedResult("D49", milesTotal);
  setCachedResult("F49", milesTotal * 0.725);
  setCachedResult("F50", expTotal);
  const startMin = minutesOf(guardStartTime);
  const endMin = minutesOf(guardEndTime);
  if (startMin != null && endMin != null) {
    const rawHours = ((endMin - startMin + 1440) % 1440) / 60;
    const hoursCharged = Math.round(Math.max(4, rawHours) * 100) / 100;
    setCachedResult("D48", rawHours);
    setCachedResult("E48", hoursCharged);
    setCachedResult("F48", hoursCharged * 40);
    setCachedResult("L50", hoursCharged * 40 + milesTotal * 0.725 + expTotal);
  }

  const out = await wb.xlsx.writeBuffer();
  return new Blob([out], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
}

/** Trigger a download of the filled workbook. */
export async function downloadMalcaWorkbook(job, broker) {
  const blob = await generateMalcaWorkbook(job, broker);
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${job.jobNumber || job.id || "job"}-Malca.xlsx`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
